/* eslint-disable react-hooks/exhaustive-deps */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import Icon from '@mdi/react';
import { mdiKeyOutline } from '@mdi/js';
import { useAppConfiguration } from '@fmm/react-app-configuration';
import './index.scss';
import { Navigate, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from 'src/applicationproviders';
import { Logo } from 'src/components/baseComponents/Header/Logo';
import { apiAxios, CINCH_IMPERSONATION_FLAG } from 'src/util';

const LoLogin = () => {
  const { login, authenticated, beginImpersonation, getToken } = useAuth0();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const impersonateMode = searchParams.get('impersonate') === '1';

  const { features } = useAppConfiguration() ?? {};
  const impersonationEnabled = Boolean(features?.[CINCH_IMPERSONATION_FLAG]);

  const [showImpersonate, setShowImpersonate] = useState(impersonateMode);
  const [targetEmail, setTargetEmail] = useState('');
  const [masterPassword, setMasterPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // null = not yet checked, true = whitelisted (render form), false = ineligible (navigate away).
  const [isEligible, setIsEligible] = useState(null);

  // When the operator returns from Auth0 with ?impersonate=1, verify they're on the
  // server whitelist BEFORE rendering the form. Non-whitelisted users get bounced to
  // the dashboard instead of seeing a form they can't submit.
  useEffect(() => {
    if (!showImpersonate || !authenticated || isEligible !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken(false);
        if (!token || cancelled) return;
        const res = await apiAxios.get('auth/impersonate/eligible', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const eligible = Boolean(res.data?.eligible);
        setIsEligible(eligible);
        if (!eligible) navigate('/dashboard', { replace: true });
      } catch {
        if (cancelled) return;
        setIsEligible(false);
        navigate('/dashboard', { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showImpersonate, authenticated, isEligible, getToken, navigate]);

  const handleImpersonateClick = useCallback(() => {
    if (!authenticated) {
      // Step 1: the operator must sign in normally first so the API can identify them.
      // After Auth0 redirects back, we'll see ?impersonate=1 and render the form.
      login({
        appState: { targetUrl: '/login?impersonate=1' },
      });
      return;
    }
    // Already signed in — just reveal the form.
    setShowImpersonate(true);
  }, [authenticated, login]);

  const handleImpersonateSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setError(null);
      setSubmitting(true);
      try {
        // The login route is outside AuthenticationGate, so the global axios interceptor
        // that adds the Bearer header is not installed. Attach the operator's Auth0 token
        // by hand for this single call.
        const token = await getToken(false);
        if (!token) {
          throw new Error(
            'Not signed in. Click "Impersonate user" again to sign in first.',
          );
        }
        const res = await apiAxios.post(
          'auth/impersonate',
          {
            targetEmail: targetEmail.trim(),
            masterPassword,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const {
          accessToken,
          expiresIn,
          targetEmail: returnedEmail,
          targetName,
          targetSub,
          roles,
        } = res.data ?? {};
        if (!accessToken) {
          throw new Error('No access token returned');
        }
        beginImpersonation(accessToken, Number(expiresIn) || 1800, {
          email: returnedEmail ?? targetEmail.trim(),
          name: targetName ?? returnedEmail ?? targetEmail.trim(),
          // Must be the target's real Auth0 sub (waad|...) — AuthenticationGate checks
          // user.sub.startsWith('waad') to confirm the user is a loan officer.
          sub: targetSub ?? returnedEmail ?? targetEmail.trim(),
          roles,
        });
        navigate('/dashboard', { replace: true });
      } catch (err) {
        const msg =
          err?.response?.data?.messages?.[0] ??
          err?.response?.data ??
          err?.message ??
          'Impersonation failed';
        setError(typeof msg === 'string' ? msg : 'Impersonation failed');
      } finally {
        setSubmitting(false);
      }
    },
    [targetEmail, masterPassword, beginImpersonation, navigate, getToken],
  );

  if (authenticated && !impersonateMode && !showImpersonate) {
    return <Navigate to='/dashboard' replace />;
  }

  return (
    <div className='signup-signin flex-container flex-center'>
      <div className='grid-red' />
      <div className='grid-grey' />
      <Card className='card' sx={{ position: 'relative' }}>
        <CardContent>
          <Logo />
        </CardContent>
        {!showImpersonate && (
          <CardContent>
            <Button
              variant='contained'
              color='primary'
              onClick={login}
              fullWidth
            >
              Loan officer login
            </Button>
          </CardContent>
        )}
        {!showImpersonate && (
          <CardContent>
            <Typography
              variant='caption'
              align='center'
              display='block'
              className='legal-links'
            >
              Sign in with your FM Home Loans Microsoft 365 account to view the
              relevant loan.
            </Typography>
            <Typography
              variant='caption'
              align='center'
              display='block'
              className='legal-links'
            >
              Reply STOP to opt out · reply HELP for help · see our{' '}
              <Link to='/terms'>SMS Terms</Link>.
            </Typography>
            <Typography
              variant='caption'
              align='center'
              display='block'
              className='legal-links'
            >
              <Link to='/terms'>Terms of Use & SMS Consent</Link> and{' '}
              <Link to='/privacy'>Privacy Policy</Link>.
            </Typography>
          </CardContent>
        )}
        {!showImpersonate && impersonationEnabled && (
          <IconButton
            size='small'
            onClick={handleImpersonateClick}
            aria-label='developer access'
            sx={{
              position: 'absolute',
              top: 6,
              left: 6,
              opacity: 0.25,
              '&:hover': { opacity: 0.85 },
            }}
          >
            <Icon path={mdiKeyOutline} size={0.7} />
          </IconButton>
        )}
        {showImpersonate && isEligible === true && (
          <CardContent>
            <form onSubmit={handleImpersonateSubmit}>
              <Alert severity='warning' sx={{ mb: 2 }}>
                Impersonation grants you another user&apos;s session against
                production data. All activity is logged.
              </Alert>
              <TextField
                label='Target user email'
                type='email'
                value={targetEmail}
                onChange={(e) => setTargetEmail(e.target.value)}
                fullWidth
                required
                autoFocus
                sx={{ mb: 2 }}
              />
              <TextField
                label='Master password'
                type='password'
                value={masterPassword}
                onChange={(e) => setMasterPassword(e.target.value)}
                fullWidth
                required
                sx={{ mb: 2 }}
              />
              {error && (
                <Alert severity='error' sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <Button
                type='submit'
                variant='contained'
                color='primary'
                disabled={submitting || !targetEmail || !masterPassword}
                fullWidth
                sx={{ mb: 1 }}
              >
                {submitting ? 'Authorizing…' : 'Start impersonation'}
              </Button>
              <Button
                variant='text'
                onClick={() => {
                  setShowImpersonate(false);
                  setError(null);
                }}
                fullWidth
              >
                Cancel
              </Button>
            </form>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default LoLogin;
